import { datadogRum } from '@datadog/browser-rum';

import { classifyRequestError, type RequestErrorKind } from '@/shared/api';

/**
 * Start / outcome / elapsed-time telemetry for one attempt at a user workflow step, sent to RUM.
 *
 * Each attempt emits a `<workflow>.<step>.started` action, then at most one `<workflow>.<step>`
 * duration vital tagged with its `outcome`. A started attempt with no vital was abandoned (tab
 * closed, navigation) before it settled.
 *
 * `beforeSendRum` does not scrub custom context, so context only takes primitives and callers must
 * pass opaque ids, counts and enums — never emails, names, addresses or amounts.
 */
export type WorkflowOutcome = 'success' | 'business_rejection' | 'app_error';

export type WorkflowContext = Record<string, string | number | boolean | undefined>;

export type WorkflowAttempt = {
  succeed: (context?: WorkflowContext) => void;
  /** Classifies `error` (4xx → business rejection, anything else → app error) and records it. */
  fail: (error: unknown, context?: WorkflowContext) => void;
};

// A 4xx is the backend refusing the request on its merits (validation, conflict). Anything else —
// a 5xx, no response, or our own exception — means the workflow broke.
const outcomeFor = (kind: RequestErrorKind): WorkflowOutcome =>
  kind === 'client_error' ? 'business_rejection' : 'app_error';

const compact = (context: WorkflowContext) =>
  Object.fromEntries(Object.entries(context).filter(([, value]) => value !== undefined));

export const startWorkflowAttempt = (
  workflow: string,
  step: string,
  context: WorkflowContext = {},
): WorkflowAttempt => {
  const name = `${workflow}.${step}`;
  const baseContext = { workflow, step, ...context };
  const startTime = Date.now();
  const startedAt = performance.now();
  let settled = false;

  datadogRum.addAction(`${name}.started`, compact(baseContext));

  // Returns false once the attempt has settled: the first outcome wins, so a throw after success
  // can't also report the attempt as failed.
  const settle = (outcome: WorkflowOutcome, outcomeContext: WorkflowContext) => {
    if (settled) {
      return false;
    }
    settled = true;
    datadogRum.addDurationVital(name, {
      startTime,
      duration: performance.now() - startedAt,
      context: compact({ ...baseContext, ...outcomeContext, outcome }),
    });
    return true;
  };

  return {
    succeed: (outcomeContext = {}) => {
      settle('success', outcomeContext);
    },
    fail: (error, outcomeContext = {}) => {
      const { kind, httpStatus, errorCode } = classifyRequestError(error);
      const failureContext = {
        ...outcomeContext,
        failure_reason: kind,
        http_status: httpStatus,
        error_code: errorCode,
      };
      // HTTP failures already appear as RUM resources; our own exceptions would otherwise leave no
      // stack anywhere.
      if (settle(outcomeFor(kind), failureContext) && kind === 'unknown') {
        datadogRum.addError(error, compact({ ...baseContext, ...failureContext }));
      }
    },
  };
};
